import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import Header from '../../components/common/Header';
import Sidebar from '../../components/common/Sidebar';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { Bot, Send, User, Sparkles, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './PolicyChat.css';

const PolicyChat = () => {
  const { employee } = useAuth();
  const deptAvatar = employee?.department_detail?.avatar_image;
  const deptName = employee?.department_detail?.name;

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hrProfile, setHrProfile] = useState(null);

  const { addToast } = useToast();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchActiveHR();
    fetchChatHistory();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchActiveHR = async () => {
    try {
      const res = await api.get('/hr/active/');
      setHrProfile(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchChatHistory = async () => {
    try {
      setInitialLoading(true);
      const res = await api.get('/chats/');
      const sessions = res.data.results || res.data;
      const policySession = sessions.find(s => s.chat_type === 'POLICY');
      if (policySession) {
        setSessionId(policySession.id);
        setMessages(policySession.messages || []);
      } else {
        // Welcome initial greeting message from HR Persona
        const hrName = hrProfile?.name || 'Sarah Jenkins';
        const hrDesig = hrProfile?.designation || 'Chief HR Officer';
        const hrCompany = hrProfile?.company_name || 'Technaureus Info Solutions';
        const welcomeMsg = hrProfile?.greeting_message || `Hello! I am ${hrName}, ${hrDesig} at ${hrCompany}. Ask me anything about company leave policies, work-from-home rules, office timings, or IT security policies.`;
        setMessages([{
          id: 'welcome',
          sender: 'AI',
          message: welcomeMsg,
          sources: []
        }]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userQ = input.trim();
    setInput('');

    // Append user message immediately
    const tempUserMsg = { id: Date.now(), sender: 'USER', message: userQ };
    setMessages(prev => [...prev, tempUserMsg]);

    setLoading(true);
    try {
      const res = await api.post('/ai/policy/', {
        session_id: sessionId,
        question: userQ
      });

      if (res.data.session_id) {
        setSessionId(res.data.session_id);
      }

      const aiMsg = {
        id: res.data.message?.id || Date.now() + 1,
        sender: 'AI',
        message: res.data.answer,
        sources: res.data.sources || []
      };

      setMessages(prev => [...prev, aiMsg]);
    } catch (err) {
      console.error(err);
      addToast('Failed to reach MentorAI Policy Assistant.', 'error');
      setMessages(prev => [...prev, {
        id: Date.now() + 2,
        sender: 'AI',
        message: 'Unable to connect to MentorAI right now. Please try again later.',
        sources: []
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-layout-root">
      <Header />
      <div className="page-body-wrapper">
        <Sidebar />
        <main className="chat-main-wrapper">
          
          {/* Header */}
          <div className="chat-header-bar">
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/company-policies')}>
              <ArrowLeft size={14} /> Policies Dashboard
            </button>
            <div className="chat-header-left">
              <div className="chat-header-avatar-box">
                {hrProfile?.avatar_url || hrProfile?.avatar || deptAvatar ? (
                  <img src={hrProfile?.avatar_url || hrProfile?.avatar || deptAvatar} alt={hrProfile?.name || 'HR Avatar'} className="chat-header-avatar-img" />
                ) : (
                  <Bot size={20} />
                )}
                <span className="online-dot"></span>
              </div>
              <div>
                <h1 className="chat-header-title">
                  {hrProfile?.name ? `${hrProfile.name} • ${hrProfile.designation}` : 'HR Policy Assistant'}
                </h1>
                <span className="chat-header-tag">
                  <Sparkles size={12} /> {hrProfile?.company_name || 'Technaureus Info Solutions'} • Grounded in official policies
                </span>
              </div>
            </div>
          </div>

          {/* Chat Container */}
          <div className="card chat-card-container">
            
            {/* Scrollable messages area */}
            <div className="chat-messages-scroll">
              {initialLoading ? <LoadingSpinner label="Loading chat history..." /> : (
                messages.map((msg, index) => {
                  const isUser = msg.sender === 'USER';
                  const hrImg = hrProfile?.avatar_url || hrProfile?.avatar || deptAvatar;

                  return (
                    <div key={msg.id || index} className={`chat-message-row ${isUser ? 'user' : 'ai'}`}>
                      <div className={`chat-message-avatar ${isUser ? 'user' : 'ai'}`}>
                        {isUser ? (
                          <User size={16} />
                        ) : hrImg ? (
                          <img src={hrImg} alt={hrProfile?.name || 'HR'} className="chat-message-avatar-img" />
                        ) : (
                          <Bot size={16} />
                        )}
                      </div>

                      <div className={`chat-message-bubble ${isUser ? 'user' : 'ai'}`}>
                        {!isUser && (
                          <div className="ai-sender-name">
                            {hrProfile?.name || 'Sarah Jenkins'} <span className="ai-sender-role">({hrProfile?.company_name || 'Technaureus'})</span>
                          </div>
                        )}
                        {msg.message}

                        {!isUser && msg.sources && msg.sources.length > 0 && (
                          <div className="chat-sources-box">
                            {msg.sources.map((src, idx) => (
                              <span key={idx} className="badge badge-info" style={{ fontSize: '0.65rem' }}>
                                Source: {src}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}

              {loading && (
                <div className="chat-thinking-row">
                  <div className="chat-message-avatar ai">
                    {deptAvatar ? (
                      <img src={deptAvatar} alt="AI Lead" className="chat-message-avatar-img" />
                    ) : (
                      <Bot size={16} />
                    )}
                  </div>
                  <span>{deptName ? `${deptName} AI Lead is thinking...` : 'MentorAI is thinking & checking policy documents...'}</span>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Bar */}
            <form onSubmit={handleSend} className="chat-input-form">
              <input
                type="text"
                className="form-input chat-input-field"
                placeholder="Ask about leave policy, WFH, timings, code of conduct..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={loading}
              />
              <button type="submit" className="btn btn-primary chat-send-btn" disabled={loading || !input.trim()}>
                <Send size={18} />
              </button>
            </form>

          </div>

        </main>
      </div>
    </div>
  );
};

export default PolicyChat;
